import React, { PropsWithChildren, PureComponent } from 'react'
import { css, cx } from '@emotion/css'
import { MessageDescriptor } from 'react-intl'
import { H2 } from '../../heading'
import { theme as deprecatedTheme } from '../../../styled'
import { theme } from '@contrast-security-inc/design-system-foundations'
import {
  Order,
  OrderDirection,
  OrderFieldConfig,
  OrderSelector
} from '../../order'
import { SearchTextInput } from '../../search-text'
import { QuickFilter, QuickFilterDropdown } from '../../quick-filter'
import {
  SessionMetadataField,
  SessionMetadataFieldDropdown
} from '../../../components/session-metadata-field'
import { ContrastFormattedMessage } from '../../../components/contrast-formatted-message'

interface Props<TOrderField extends string> {
  readonly titleMessage: MessageDescriptor
  readonly searchPlaceholderMessage: MessageDescriptor
  readonly initialFilterText: string
  readonly style: {
    readonly [k: string]: string
  }
  readonly order: Order<TOrderField>
  readonly onSetSortField: (field: TOrderField) => void
  readonly onSetSortDirection: (direction: OrderDirection) => void
  readonly orderFieldConfig: OrderFieldConfig<TOrderField>
  readonly defaultOrderField?: TOrderField
  readonly onFilterTextChange: (value: string) => void
  readonly onFilterTextFocus?: () => void
  readonly onQuickFilterIDChange: (quickFilterID: string) => void
  readonly quickFilterID: string
  readonly quickFilters?: QuickFilter[]
  readonly quickFilterToGroup?: (filter: QuickFilter) => string
  readonly showQuickFilter?: (filter: QuickFilter) => boolean
  readonly isDrilldown: boolean
  readonly isMinimalGrid?: boolean
  readonly sessionMetadataFieldID?: string
  readonly sessionMetadataFields?: SessionMetadataField[]
  readonly sessionMetadataFieldToGroup?: (field: SessionMetadataField) => string
  readonly onSessionMetadataFieldIDChange?: (
    sessionMetadataFieldID: string
  ) => void
  readonly extraChildren?: React.ReactNode
  readonly headerCenteredChildren?: React.ReactNode
  readonly headerPreOrderSelectorChildren?: React.ReactNode
  readonly contentBelowHeader?: React.ReactNode
}

export const gridHeaderHeight = 100
export const gridHeaderDrilldownHeight = 50
export const gridHeaderContentBelowHeight = 464
export const gridHeaderContentFull = 764

const headerStyle = css`
  display: flex;
  align-items: center;
  padding-top: 4px;
  flex-direction: column;
`

const headerInnerContainerSharedCss = css`
  display: flex;
  flex-direction: row;
  align-items: center;
  flex: 1 0 auto;
  width: 100%;
`

const headerInnerContainerCss = cx(
  headerInnerContainerSharedCss,
  css`
    height: ${gridHeaderHeight - 20}px;
    margin-bottom: 20px;
    background-color: ${theme.color.white};
    border-bottom: 1px solid ${theme.color.neutral40};
    padding: 0 60px 0 65px;
  `
)

const headerInnerDrilldownContainerCss = cx(
  headerInnerContainerSharedCss,
  css`
    padding: 0 54px 0 78px;
    margin: 3px 0 3px 0;
  `
)

const titleStyle = css`
  margin: 0;
  margin-right: ${deprecatedTheme.space[1]}px;
  font-family: ${deprecatedTheme.fontFamilies.primaryCondensed};
  font-weight: bold;
`

const searchStyle = css`
  padding-top: ${deprecatedTheme.space[1]}px;
  margin-left: ${deprecatedTheme.space[1]}px;
  margin-right: ${deprecatedTheme.space[1]}px;
`

const sessionMetadataFieldsStyle = css`
  display: flex;
  margin-left: auto;
`

const headerCenteredChildrenCss = css`
  display: flex;
  margin: 0 auto;
  @media (max-width: 1024px) {
    display: none;
  }
`

const contentBelowHeaderCss = css`
  margin-top: 15px;
  margin-bottom: 35px;
  width: 91%;
`
const headerContainerCss = (isDrillDown: boolean, isMinimalGrid?: boolean) => {
  if (isDrillDown) {
    const drillDownCss = headerInnerDrilldownContainerCss
    if (isMinimalGrid) {
      cx(
        headerInnerDrilldownContainerCss,
        css`
          padding: 0 45px 0 28px;
        `
      )
    }
    return drillDownCss
  }
  return headerInnerContainerCss
}

type GridHeaderState = {
  contentBelowHeaderRef: React.RefObject<HTMLDivElement>
}
export class GridHeader<TOrderField extends string> extends PureComponent<
  PropsWithChildren<Props<TOrderField>>,
  GridHeaderState
> {
  public render() {
    const {
      titleMessage,
      searchPlaceholderMessage,
      style,
      order,
      onSetSortField,
      onSetSortDirection,
      orderFieldConfig,
      defaultOrderField,
      onFilterTextChange,
      onFilterTextFocus,
      initialFilterText,
      onQuickFilterIDChange,
      quickFilterID,
      quickFilters,
      quickFilterToGroup,
      showQuickFilter,
      isDrilldown,
      isMinimalGrid,
      sessionMetadataFieldID,
      sessionMetadataFields,
      sessionMetadataFieldToGroup,
      onSessionMetadataFieldIDChange,
      extraChildren,
      headerCenteredChildren,
      headerPreOrderSelectorChildren,
      contentBelowHeader
    } = this.props

    const sessionMetadataExists: boolean =
      !!sessionMetadataFields &&
      !!sessionMetadataFields.filter(
        (field) => field.id !== 'dissociated' && field.count !== 0
      ).length

    return (
      <header style={style} className={headerStyle}>
        <div className={headerContainerCss(isDrilldown, isMinimalGrid)}>
          {!isDrilldown && (
            <ContrastFormattedMessage {...titleMessage}>
              {(message) => (
                <H2 className={titleStyle} data-e2e="grid-header-name">
                  {message}
                </H2>
              )}
            </ContrastFormattedMessage>
          )}
          {quickFilters && (
            <QuickFilterDropdown
              filters={quickFilters}
              selectedFilter={quickFilterID}
              onChange={onQuickFilterIDChange}
              filterToGroup={quickFilterToGroup}
              showQuickFilter={showQuickFilter}
              isDrilldown={isDrilldown}
            />
          )}
          <div className={searchStyle}>
            <SearchTextInput
              placeholderI18n={searchPlaceholderMessage}
              onChange={onFilterTextChange}
              onFocus={onFilterTextFocus}
              defaultValue={initialFilterText}
            />
          </div>
          {sessionMetadataExists &&
            !!sessionMetadataFields &&
            !!sessionMetadataFields.length &&
            !!onSessionMetadataFieldIDChange &&
            !!sessionMetadataFieldID && (
              <div
                className={sessionMetadataFieldsStyle}
                data-testid="session-metadata-fields-container"
              >
                <SessionMetadataFieldDropdown
                  fields={sessionMetadataFields}
                  selectedFieldID={sessionMetadataFieldID}
                  onChange={onSessionMetadataFieldIDChange}
                  filterToGroup={sessionMetadataFieldToGroup}
                />
              </div>
            )}
          <div className={headerCenteredChildrenCss}>
            {headerCenteredChildren}
          </div>
          <div
            className={css`
              display: flex;
              align-items: center;
            `}
          >
            <div
              className={css`
                margin-right: 15px;
              `}
            >
              {headerPreOrderSelectorChildren}
            </div>
            <OrderSelector
              order={order}
              onSetSortField={onSetSortField}
              onSetSortDirection={onSetSortDirection}
              orderFieldConfig={orderFieldConfig}
              defaultField={defaultOrderField}
              containerClassName={
                sessionMetadataExists &&
                !!sessionMetadataFields &&
                !!sessionMetadataFields.length
                  ? css`
                      margin-left: ${deprecatedTheme.space[1]}px;
                    `
                  : undefined
              }
            />
          </div>
          {extraChildren}
        </div>
        <div className={contentBelowHeaderCss}>{contentBelowHeader}</div>
      </header>
    )
  }
}
